import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, Loader2, LogOut, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { UnitProvider, useUnitContext } from '@/contexts/UnitContext';

function Inner() {
  const { logout, userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const { availableUnits, availableUnitsLoading } = useUnitContext();

  useEffect(() => {
    if (!currentUser) navigate('/login');
  }, [currentUser, navigate]);

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="text-lg font-semibold text-slate-900">Treasurer</div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-600 hidden sm:block">
              {userProfile?.firstName ?? userProfile?.email}
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Choose a Lodge</h1>
          <p className="text-slate-600">
            Select the lodge whose finances you wish to manage.
          </p>
        </div>

        {availableUnitsLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mb-3" />
            <p className="text-sm">Loading your lodges…</p>
          </div>
        ) : availableUnits.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-900 mb-1">
              No units yet
            </h2>
            <p className="text-slate-600 mb-5">
              We could not find any lodge linked to your account (including legacy
              memberships). If you should be listed on a lodge, ask your Secretary to
              confirm you appear under that lodge&apos;s members, or create a new lodge.
            </p>
            <Link
              to="/unit/create"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" /> Create lodge
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableUnits.map((u) => (
              <Link
                key={u.unitId}
                to={`/u/${u.unitId}/dashboard`}
                className="group flex items-center justify-between bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-400 hover:shadow-sm transition-all"
              >
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    {u.name}
                  </div>
                  <div className="text-sm text-slate-500">
                    No. {u.number || '—'} · {u.role}
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-emerald-600" />
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function UnitsListPage() {
  return (
    <UnitProvider>
      <Inner />
    </UnitProvider>
  );
}
